import { DashboardStatCard } from 'app/shared/cards/stat/dashboard-stat';
import { getRelativeTimeStampRange } from 'app/shared/utils/date';
import { useMailActionService } from 'hooks/mailaction';
import { useMailLogService } from 'hooks/maillog';
import { useContactService } from 'hooks/users/contact';
import React, { useEffect, useState } from 'react';
import styles from './stats.module.css';

const pctChange = (current, previous) => {
  if (!previous || previous === 0) return null;
  return Math.round(((current - previous) / previous) * 100);
};

const trendPill = (value, tone_if_positive, tone_if_negative) => {
  if (value === null) return null;
  const sign = value > 0 ? '+' : '';
  const tone = value > 0 ? tone_if_positive : value < 0 ? tone_if_negative : 'neutral';
  return { text: `${sign}${value}`, tone };
};

export const StatsBar = () => {
  const { fetchContacts } = useContactService();
  const { fetchMailActions } = useMailActionService();
  const { fetchMailLogs } = useMailLogService();

  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState({ total: null, new_this_month: null });
  const [sent, setSent] = useState({ this_month: null, last_month: null });
  const [opens, setOpens] = useState({ this_month: null, last_month: null });
  const [unsubs, setUnsubs] = useState({ this_month: null, last_month: null });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    const { this_month, last_month } = getRelativeTimeStampRange();

    const [
      { size: total_contacts },
      { size: new_this_month },
      { size: sent_this_month },
      { size: sent_last_month },
      { size: opens_this_month },
      { size: opens_last_month },
      { size: unsubs_this_month },
      { size: unsubs_last_month }
    ] = await Promise.all([
      fetchContacts({ query_string: 'count=1' }),
      fetchContacts({
        query_string: `time_stamp=${this_month.start}~${this_month.end}&count=1`
      }),
      fetchMailLogs({
        query_string: `time_stamp=${this_month.start}~${this_month.end}&count=1`
      }),
      fetchMailLogs({
        query_string: `time_stamp=${last_month.start}~${last_month.end}&count=1`
      }),
      fetchMailActions({
        query_string: `action=opened&time_stamp=${this_month.start}~${this_month.end}&count=1`
      }),
      fetchMailActions({
        query_string: `action=opened&time_stamp=${last_month.start}~${last_month.end}&count=1`
      }),
      fetchMailActions({
        query_string: `action=unsubscribed&time_stamp=${this_month.start}~${this_month.end}&count=1`
      }),
      fetchMailActions({
        query_string: `action=unsubscribed&time_stamp=${last_month.start}~${last_month.end}&count=1`
      })
    ]);

    setContacts({ total: total_contacts, new_this_month });
    setSent({ this_month: sent_this_month, last_month: sent_last_month });
    setOpens({ this_month: opens_this_month, last_month: opens_last_month });
    setUnsubs({ this_month: unsubs_this_month, last_month: unsubs_last_month });
    setLoading(false);
  };

  const open_rate_this =
    sent.this_month > 0 ? Math.round((opens.this_month / sent.this_month) * 100) : null;
  const open_rate_last =
    sent.last_month > 0 ? Math.round((opens.last_month / sent.last_month) * 100) : null;

  const sent_pct = pctChange(sent.this_month, sent.last_month);
  const open_rate_pp =
    open_rate_this !== null && open_rate_last !== null
      ? Math.round(open_rate_this - open_rate_last)
      : null;
  const unsub_delta = unsubs.last_month !== null ? unsubs.this_month - unsubs.last_month : null;

  return (
    <div className={styles.grid}>
      <DashboardStatCard
        label="Contacts"
        value={loading ? null : contacts.total?.toLocaleString()}
        pill={
          !loading && contacts.new_this_month > 0
            ? { text: `+${contacts.new_this_month.toLocaleString()} this month`, tone: 'good' }
            : !loading
            ? { text: 'No new contacts this month', tone: 'neutral' }
            : null
        }
        loading={loading}
      />
      <DashboardStatCard
        label="Emails sent"
        value={loading ? null : sent.this_month?.toLocaleString()}
        pill={
          !loading && sent_pct !== null
            ? trendPill(sent_pct, 'good', 'bad')
              ? {
                  text: `${sent_pct > 0 ? '+' : ''}${sent_pct}% vs last month`,
                  tone: sent_pct > 0 ? 'good' : sent_pct < 0 ? 'bad' : 'neutral'
                }
              : null
            : null
        }
        context={!loading && sent_pct === null ? 'No data last month' : undefined}
        loading={loading}
      />
      <DashboardStatCard
        label="Open rate"
        value={loading ? null : open_rate_this !== null ? `${open_rate_this}%` : '—'}
        pill={
          !loading && open_rate_pp !== null
            ? {
                text: `${open_rate_pp > 0 ? '+' : ''}${open_rate_pp}pp vs last month`,
                tone: open_rate_pp > 0 ? 'good' : open_rate_pp < 0 ? 'bad' : 'neutral'
              }
            : null
        }
        context={
          !loading && open_rate_this === null
            ? 'Appears after first send'
            : !loading && open_rate_pp === null && open_rate_this !== null
            ? 'No data last month'
            : undefined
        }
        loading={loading}
      />
      <DashboardStatCard
        label="Unsubscribes"
        value={loading ? null : unsubs.this_month?.toLocaleString()}
        pill={
          !loading && unsub_delta !== null && unsub_delta !== 0
            ? {
                text: `${unsub_delta > 0 ? '+' : ''}${unsub_delta} vs last month`,
                tone: unsub_delta > 0 ? 'bad' : 'good'
              }
            : !loading && unsub_delta === 0
            ? { text: 'Same as last month', tone: 'neutral' }
            : null
        }
        loading={loading}
      />
    </div>
  );
};
